import { ACTION_NAME, IRequestOptions, RequestService } from '@ads/iam-library';
import {
    DeleteUploadedVideoResponse,
    StatusResponse,
    UploadInfoRequest,
    UploadInfoResponse,
} from '@/services/video/creativeHubApiTypes';
import { VideoUploadInfo } from '@/services/video/VideoUploadInfo';
import { VAST_GENERATION_STATUS } from '@/services/video/VAST_GENERATION_STATUS';
import { VideoMetadata } from '@/services/video/VideoMetadata';

export default class VideoUploadService {
    private static readonly VIDEO_FILE_SIZE_LIMIT_IN_MB = 30;

    private readonly vastGenerationStatusByApiStatus = new Map<string, VAST_GENERATION_STATUS>([
        ['Upload completed, waiting for transcode', VAST_GENERATION_STATUS.TRANSCODING_WAITING],
        ['Transcode completed, creating VAST file', VAST_GENERATION_STATUS.TRANSCODING_COMPLETED],
        ['FAILED', VAST_GENERATION_STATUS.FAILED],
        ['ABORTED', VAST_GENERATION_STATUS.ABORTED],
        ['Finished', VAST_GENERATION_STATUS.SUCCEEDED],
    ]);

    private readonly creativeHubService: RequestService;

    constructor(requestService: RequestService) {
        this.creativeHubService = requestService;
    }

    public async prepareForUpload(file: File, clickUrl: string): Promise<VideoUploadInfo> {
        await this.validateVideoFile(file);

        const requestOptions = this.getRequestOptions(ACTION_NAME.CREATE);
        const body = this.buildUploadRequestBody(file.name, clickUrl);

        try {
            const response = await this.creativeHubService.post<UploadInfoRequest, UploadInfoResponse>(
                '/api/transcode',
                body,
                requestOptions,
            );

            return {
                uploadUrl: response.signedUrl,
                videoId: response.documentId,
                vastFileUrl: response.vastFileUrl,
            };
        } catch {
            throw new Error('Failed to prepare for video upload');
        }
    }

    public async deleteUploadedVideo(videoId: string): Promise<void> {
        const requestOptions = this.getRequestOptions(ACTION_NAME.REMOVE);

        try {
            await this.creativeHubService.delete<DeleteUploadedVideoResponse>(`/api/transcode/${videoId}`, requestOptions);
        } catch {
            throw new Error('Failed to remove the uploaded video');
        }
    }

    public async getVastGenerationStatus(videoId: string): Promise<VAST_GENERATION_STATUS> {
        const requestOptions = this.getRequestOptions(ACTION_NAME.READ);

        try {
            const response = await this.creativeHubService.get<StatusResponse>(
                `/api/transcode/${videoId}/status`,
                requestOptions,
            );
            return this.vastGenerationStatusByApiStatus.get(response.status);
        } catch {
            throw new Error('Failed to get status');
        }
    }

    private getRequestOptions(action: ACTION_NAME): IRequestOptions {
        return {
            domain: 'creative-hub',
            resource: 'transcode',
            action,
        };
    }

    private async validateVideoFile(file: File): Promise<void> {
        if (file.size > VideoUploadService.VIDEO_FILE_SIZE_LIMIT_IN_MB * 1_000 * 1_000) {
            throw new Error(
                `Failed to prepare for video upload: file size exceeds ${VideoUploadService.VIDEO_FILE_SIZE_LIMIT_IN_MB} MB`,
            );
        }
        const videoMetadata = await this.getVideoMetadata(file);
        if (videoMetadata.width / videoMetadata.height !== 16 / 9) {
            throw new Error('Failed to prepare for video upload: aspect ratio should be 16:9');
        }
        if (videoMetadata.durationInSeconds > 30) {
            throw new Error('Failed to prepare for video upload: duration exceeds 30 seconds');
        }
    }

    private buildUploadRequestBody(fileName: string, clickUrl: string): UploadInfoRequest {
        return {
            ext: 'mp4',
            contentType: 'video/mp4',
            sizes: [
                { width: 1920, height: 1080 },
                { width: 1280, height: 720 },
                { width: 640, height: 360 },
            ],
            file: fileName,
            clickUrl,
        };
    }

    private async getVideoMetadata(file: File): Promise<VideoMetadata> {
        const video = this.createVideoElement(file);

        return new Promise((resolve) => {
            video.addEventListener('loadedmetadata', () => {
                const metadata = this.extractVideoMetadata(video);
                this.destroyVideoElement(video);
                resolve(metadata);
            });
        });
    }

    private createVideoElement(file: File): HTMLVideoElement {
        const videoURL = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.src = videoURL;
        return video;
    }

    private destroyVideoElement(video: HTMLVideoElement): void {
        URL.revokeObjectURL(video.src);
        video.remove();
    }

    private extractVideoMetadata(video: HTMLVideoElement): VideoMetadata {
        return {
            width: video.videoWidth,
            height: video.videoHeight,
            durationInSeconds: video.duration,
        };
    }
}
